import { motion } from 'framer-motion';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useTheme } from '../hooks/useTheme';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { ThemeSelector } from './ThemeSelector';
import { MonitorIcon, MoonIcon, PanelLeftIcon, PlusIcon, SunIcon, ToolIcon } from './icons';
import { ReportBasketButton } from './report/ReportBasketButton';
import { ActionMenu } from './visuals/ActionMenu';
import { NotificationsBell } from './notifications/NotificationsBell';
import { AgentsButton } from './agents/AgentsButton';
import { CENTRE_COMPACT_QUERY } from './notifications/useNotifications.js';
import './Header.css';

const THEMES = [
  { id: 'light', label: 'Clair', Icon: SunIcon },
  { id: 'dark', label: 'Sombre', Icon: MoonIcon },
  { id: 'auto', label: 'Auto (suit le système)', Icon: MonitorIcon },
];

/**
 * Thème en menu (menuitemradio) sous 480 px : l'en-tête n'a plus la place du contrôle
 * segmenté une fois la cloche ajoutée (Agents, P1). Même préférence que ThemeSelector.
 */
function ThemeMenu() {
  const { preference, setPreference } = useTheme();
  const courant = THEMES.find((t) => t.id === preference) ?? THEMES[2];
  return (
    <div className="header-theme-menu">
      <ActionMenu
        label={`Thème de l'interface : ${courant.label}`}
        icon={courant.Icon}
        compact
        items={THEMES.map((t) => ({ key: t.id, label: t.label, checked: preference === t.id, onSelect: () => setPreference(t.id) }))}
      />
    </div>
  );
}

export function Header({
  serverStatus, tools, onClearChat, sidebarOpen = false, onToggleSidebar = null, sidebarId, historyEnabled = true,
  showNewButton = true,
}) {
  const connected = serverStatus.connected;
  // Sous 480 px : la cloche seule (son tiroir porte l'onglet « Agents »), thème en menu
  const compact = useMediaQuery(CENTRE_COMPACT_QUERY);

  return (
    <motion.header
      className="header"
      initial={{ y: -8, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.36, ease: [0, 0, 0, 1] }}
    >
      <div className="header-content">
        <div className="header-brand">
          {onToggleSidebar && (
            <Button
              iconOnly
              icon={PanelLeftIcon}
              iconSize={18}
              className="header-icon-button"
              aria-controls={sidebarId}
              aria-expanded={sidebarOpen}
              aria-label={sidebarOpen ? "Masquer l'historique" : "Afficher l'historique"}
              title={historyEnabled
                ? (sidebarOpen ? "Masquer l'historique" : "Afficher l'historique")
                : "Historique indisponible : ouvrez l'assistant depuis la plateforme digiTRANS pour enregistrer vos conversations"}
              onClick={onToggleSidebar}
            />
          )}
          <span className="brand-badge">
            <BrandMark size={20} />
          </span>
          <div className="brand-text">
            <span className="brand-title">
              digi<span className="brand-title__strong">TRANS</span>
              <span className="brand-title__sep" aria-hidden="true" />
              <span className="brand-title__product">Assistant flotte</span>
            </span>
            <span className="brand-subtitle">Pilotage de flotte assisté par l'IA</span>
          </div>
        </div>

        <div className="header-status" aria-label="État du service">
          <span
            className={`status-chip ${connected ? 'is-connected' : 'is-disconnected'}`}
            role="status"
          >
            <span className="status-dot" aria-hidden="true" />
            {connected ? 'Connecté' : 'Déconnecté'}
          </span>

          {connected && (
            <span className="status-chip status-chip--tools" title="Outils de données disponibles">
              <ToolIcon size={14} />
              <span className="ds-num">{tools.length}</span>
              {tools.length === 1 ? 'outil' : 'outils'}
            </span>
          )}
        </div>

        <div className="header-actions">
          {/* Centre des agents (P1) : cloche et bouton « Agents » avant le panier ; absents
              sans contexte complet (hors de la plateforme) */}
          <NotificationsBell />
          {!compact && <AgentsButton />}
          <ReportBasketButton />
          {compact ? <ThemeMenu /> : <ThemeSelector />}
          {showNewButton && (
            <Button variant="primary" icon={PlusIcon} className="btn-new" onClick={onClearChat}>
              <span className="btn-new__label">Nouvelle conversation</span>
            </Button>
          )}
        </div>
      </div>
    </motion.header>
  );
}
